import { APIResponseErrorNotice } from '~/feature-components/APIResponseErrorNotice';
import { Button, Flex, Heading, Input, Stack } from '@chakra-ui/react';
import { Field } from '~/design-system/fields/Field';
import { Notice } from '~/design-system/notice';
import { Text } from '~/design-system/text';
import { resetPasswordRequest } from '~/public-api/users/send-reset-password-request.server';
import { useFetcher } from '@remix-run/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ActionFunctionArgs } from '@remix-run/node';
import type { MetaFunction } from '@remix-run/react';

const passwordResetSchema = z.object({
  usernameOrEmail: z.string().min(1, 'Username or Email is required'),
});

export const action = async (action: ActionFunctionArgs) => {
  const form = passwordResetSchema.parse(await action.request.json());
  const apiResponse = await resetPasswordRequest(form, action.request);
  return apiResponse;
};

type ResetPassValue = z.infer<typeof passwordResetSchema>;

export default function PasswordReset() {
  const { formState, handleSubmit, register } = useForm<ResetPassValue>({
    resolver: zodResolver(passwordResetSchema),
  });

  const submitFetcher = useFetcher<typeof action>();

  if (submitFetcher.data?.ok) {
    return (
      <Flex width='100%' height='full'>
        <Stack width='50%' align='center' justify='center'>
          <Heading
            color='black'
            fontSize='3xl'
            textAlign='center'
            paddingBottom='8'
            lineHeight='normal'
            fontWeight='medium'
          >
            Have you forgotten your <br /> password?
          </Heading>
          <img
            src='/password-reset-banner.png'
            alt='password reset link banner'
            width={386}
            height={290}
          />
        </Stack>

        <Stack
          width='50%'
          background='white'
          align='center'
          justify='center'
          borderLeftRadius='3xl'
        >
          <Stack gap='2' alignItems='center'>
            <img
              src='/header-logo.svg'
              alt='header logo'
              width={72}
              height={72}
            />
            <Text
              color='gray.500'
              fontSize='md'
              align='center'
              paddingBottom='8'
            >
              Navace Asset Management System
            </Text>
          </Stack>
          <form
            onSubmit={handleSubmit((usernameOrEmail) => {
              submitFetcher.submit(usernameOrEmail, {
                method: 'POST',
                encType: 'application/json',
              });
            })}
          >
            <Stack align='center'>
              <Stack gap='12' width='70%'>
                <Stack gap='3'>
                  <Heading size='md' textAlign='center'>
                    Forgot Password
                  </Heading>
                  <Text
                    size='sm'
                    color='grey.500'
                    textAlign='center'
                    marginBottom='40'
                  >
                    Enter username and email to reset your password
                  </Text>
                </Stack>
                <Notice tone='positive' title='Verification Link Sent'>
                  <Text fontSize='sm' color='grey.200'>
                    We have sent a verification link to your email.
                  </Text>
                </Notice>

                <Stack gap='10'>
                  <Field label='Username / Email'>
                    <Input
                      type='text'
                      defaultValue={formState.defaultValues?.usernameOrEmail}
                      {...register('usernameOrEmail')}
                    />
                  </Field>
                  <Stack gap='5'>
                    <Text textAlign='center' fontSize='sm'>
                      Haven’t received verification link?
                    </Text>
                    <Stack align='center'>
                      <Button
                        colorScheme='blue'
                        type='submit'
                        marginBottom='10'
                        width='292px'
                        isLoading={submitFetcher.state !== 'idle'}
                      >
                        Resend Link
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Flex>
    );
  }

  return (
    <Flex width='100%' height='full'>
      <Stack width='50%' align='center' justify='center'>
        <Heading
          color='black'
          fontSize='3xl'
          textAlign='center'
          paddingBottom='8'
          lineHeight='normal'
          fontWeight='medium'
        >
          Have you forgotten your <br /> password?
        </Heading>
        <img
          src='/password-reset-banner.png'
          alt='password reset banner'
          width={386}
          height={290}
        />
      </Stack>

      <Stack
        width='50%'
        background='white'
        align='center'
        justify='center'
        borderLeftRadius='3xl'
      >
        <Stack gap='2' alignItems='center'>
          <img
            src='/header-logo.svg'
            alt='header logo'
            width={72}
            height={72}
          />
          <Text color='gray.500' fontSize='md' align='center' paddingBottom='8'>
            Navace Asset Management System
          </Text>
        </Stack>
        <form
          onSubmit={handleSubmit((usernameOrEmail) => {
            submitFetcher.submit(usernameOrEmail, {
              method: 'POST',
              encType: 'application/json',
            });
          })}
        >
          <Stack gap='12'>
            <Stack gap='3'>
              <Heading size='md' textAlign='center'>
                Forgot Password
              </Heading>
              <APIResponseErrorNotice
                fetcherData={submitFetcher.data}
                title='Password reset request failed'
              />
              <Text size='md' textAlign='center' marginBottom='40'>
                Enter username or email to reset your password
              </Text>
            </Stack>

            <Stack gap='10'>
              <Field
                label='Username / Email'
                error={formState.errors.usernameOrEmail?.message}
              >
                <Input type='text' {...register('usernameOrEmail')} />
              </Field>
              <Stack align='center'>
                <Button
                  colorScheme='blue'
                  type='submit'
                  marginBottom='10'
                  width='292px'
                  isLoading={submitFetcher.state !== 'idle'}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Flex>
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: 'Forget Password Navace' },
    {
      name: 'description',
      content: 'Password Reset request page for Navace asset management system',
    },
  ];
};
